// Utility for sanitizing HTML content to prevent XSS attacks
import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * Uses DOMPurify to strip out dangerous HTML/JavaScript while preserving safe formatting
 *
 * @param {string} dirty - The potentially unsafe HTML string
 * @param {Object} options - Optional DOMPurify configuration
 * @returns {string} - Sanitized HTML safe for rendering
 */
export const sanitizeHtml = (dirty, options = {}) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  // Default configuration - allows safe HTML formatting but blocks scripts
  const defaultConfig = {
    // Allow these tags (safe formatting)
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'span', 'div',
      'blockquote', 'code', 'pre'
    ],

    // Allow these attributes
    ALLOWED_ATTR: [
      'href', 'title', 'target', 'rel',
      'class', 'id',
      'style' // For ReactQuill alignment classes
    ],

    // Allow data URIs for inline styles (but not for src attributes)
    ALLOW_DATA_ATTR: false,

    // Keep safe HTML entities
    KEEP_CONTENT: true,

    // Return a string (not a DOM node)
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,

    // Add target="_blank" and rel="noopener noreferrer" to all links
    ADD_ATTR: ['target', 'rel'],

    // Additional security
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
  };

  // Merge default config with custom options
  const config = { ...defaultConfig, ...options };

  // Sanitize the HTML
  const clean = DOMPurify.sanitize(dirty, config);

  return clean;
};

/**
 * Sanitize HTML specifically for ReactQuill content
 * Preserves Quill editor formatting classes and styles
 *
 * @param {string} dirty - The potentially unsafe HTML from ReactQuill
 * @returns {string} - Sanitized HTML safe for rendering
 */
export const sanitizeQuillHtml = (dirty) => {
  return sanitizeHtml(dirty, {
    // Allow Quill-specific classes for text alignment
    ALLOWED_ATTR: [
      'href', 'title', 'target', 'rel',
      'class', 'id', 'style'
    ],
    // Explicitly allow alignment classes from ReactQuill
    ADD_TAGS: [],
  });
};

/**
 * Sanitize plain text (strips all HTML)
 * Use this for user input that should NOT contain any HTML
 *
 * @param {string} dirty - The potentially unsafe string
 * @returns {string} - Plain text with all HTML removed
 */
export const sanitizePlainText = (dirty) => {
  if (!dirty || typeof dirty !== 'string') {
    return '';
  }

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
    KEEP_CONTENT: true,
  });
};

/**
 * Sanitize URL to prevent javascript: and data: protocol attacks
 *
 * @param {string} url - The potentially unsafe URL
 * @returns {string} - Sanitized URL or empty string if unsafe
 */
export const sanitizeUrl = (url) => {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Trim whitespace
  const trimmed = url.trim();

  // Block dangerous protocols
  const dangerousProtocols = /^(javascript|data|vbscript):/i;
  if (dangerousProtocols.test(trimmed)) {
    console.warn('Blocked potentially dangerous URL:', trimmed);
    return '';
  }

  // Allow only safe protocols
  const safeProtocols = /^(https?|mailto|tel):/i;
  const isRelative = !trimmed.includes(':');

  if (isRelative || safeProtocols.test(trimmed)) {
    return trimmed;
  }

  console.warn('Blocked URL with unsafe protocol:', trimmed);
  return '';
};

/**
 * Configuration for DOMPurify hooks
 * Automatically adds security attributes to links
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // Set all links to open in a new tab with security attributes
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');

    // Additional security: validate href
    const href = node.getAttribute('href');
    if (href) {
      const sanitizedHref = sanitizeUrl(href);
      if (sanitizedHref) {
        node.setAttribute('href', sanitizedHref);
      } else {
        node.removeAttribute('href');
      }
    }
  }
});

export default {
  sanitizeHtml,
  sanitizeQuillHtml,
  sanitizePlainText,
  sanitizeUrl,
};
